/**
 * Admin Dashboard Screen
 * Integrated admin dashboard for the main webapp
 */

import { useState, useEffect } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { api } from '../../services/api';

type AdminSection = 'dashboard' | 'users' | 'deposits' | 'broadcast' | 'trades' | 'assets' | 'tasks' | 'withdrawals' | 'batteries' | 'transfers' | 'staking' | 'ai-trader';

interface DashboardStats {
  users: {
    total: number;
    newToday: number;
    newThisWeek: number;
  };
  trades: {
    total: number;
  };
  assets: {
    total: number;
  };
  withdrawals: {
    total: number;
    pending: number;
  };
  deposits: {
    total: {
      amount: number;
      count: number;
    };
    pending: number;
  };
  balances: {
    totalWiTon: number;
    totalUsdt: number;
  };
}

interface AdminDashboardProps {
  onNavigate: (screen: AdminSection) => void;
}

export function AdminDashboard({ onNavigate }: AdminDashboardProps) {
  const { t } = useTranslation();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.getAdminDashboard();
      setStats(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const triggerIncome = async () => {
    try {
      await api.triggerIncome();
      alert('Income accumulation triggered successfully!');
    } catch (err: any) {
      alert('Failed to trigger income: ' + (err.response?.data?.error?.message || 'Unknown error'));
    }
  };

  const triggerDeposits = async () => {
    try {
      await api.triggerDeposits();
      alert('Deposits processing triggered successfully!');
      loadDashboard(); // Refresh stats
    } catch (err: any) {
      alert('Failed to trigger deposits: ' + (err.response?.data?.error?.message || 'Unknown error'));
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ fontSize: '18px' }}>{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ color: '#ef4444', fontSize: '18px' }}>Error: {error}</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ fontSize: '18px' }}>No data available</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)',
      color: '#fff',
      paddingBottom: '80px',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 16px 12px',
        textAlign: 'center',
        background: 'rgba(10, 22, 40, 0.8)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <h1 style={{
          fontSize: '20px',
          fontWeight: 700,
          margin: 0,
        }}>
          {t('admin.dashboard')}
        </h1>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            onClick={triggerIncome}
            style={{
              background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
              border: 'none',
              borderRadius: '8px',
              padding: '6px 10px',
              color: '#fff',
              fontSize: '11px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Income
          </button>
          <button
            onClick={triggerDeposits}
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
              border: 'none',
              borderRadius: '8px',
              padding: '6px 10px',
              color: '#fff',
              fontSize: '11px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Deposits
          </button>
          <button
            onClick={loadDashboard}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '8px',
              padding: '6px 10px',
              color: '#fff',
              fontSize: '11px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            ↻
          </button>
        </div>
      </div>

      {/* Stats Grid - compact 2x2 */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '10px',
        padding: '12px 16px',
      }}>
        <div style={{
          background: 'rgba(10, 22, 40, 0.6)',
          borderRadius: '12px',
          padding: '12px',
          border: '1px solid rgba(59, 130, 246, 0.2)',
        }}>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>👥 Total Users</div>
          <div style={{ fontSize: '24px', fontWeight: 700 }}>
            {stats.users.total.toLocaleString()}
          </div>
          <div style={{ fontSize: '10px', color: '#10b981' }}>
            +{stats.users.newToday} today, +{stats.users.newThisWeek} week
          </div>
        </div>

        <div style={{
          background: 'rgba(10, 22, 40, 0.6)',
          borderRadius: '12px',
          padding: '12px',
          border: '1px solid rgba(59, 130, 246, 0.2)',
        }}>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>📈 Total Trades</div>
          <div style={{ fontSize: '24px', fontWeight: 700 }}>
            {stats.trades.total.toLocaleString()}
          </div>
        </div>

        <div style={{
          background: 'rgba(10, 22, 40, 0.6)',
          borderRadius: '12px',
          padding: '12px',
          border: '1px solid rgba(59, 130, 246, 0.2)',
        }}>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>📦 Assets</div>
          <div style={{ fontSize: '24px', fontWeight: 700 }}>
            {stats.assets.total}
          </div>
        </div>

        <div style={{
          background: 'rgba(10, 22, 40, 0.6)',
          borderRadius: '12px',
          padding: '12px',
          border: '1px solid rgba(59, 130, 246, 0.2)',
        }}>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>💰 Deposits</div>
          <div style={{ fontSize: '24px', fontWeight: 700 }}>
            {stats.deposits.total.count.toLocaleString()}
          </div>
          <div style={{ fontSize: '10px', color: '#666' }}>
            ${stats.deposits.total.amount.toLocaleString()} USDT
          </div>
          {stats.deposits.pending > 0 && (
            <div style={{ fontSize: '10px', color: '#ef4444' }}>
              {stats.deposits.pending} pending
            </div>
          )}
        </div>

        <div style={{
          background: 'rgba(10, 22, 40, 0.6)',
          borderRadius: '12px',
          padding: '12px',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          gridColumn: 'span 2',
        }}>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>💎 Total Balances</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 700 }}>{stats.balances.totalWiTon.toLocaleString()}</div>
              <div style={{ fontSize: '10px', color: '#666' }}>WiTon</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '20px', fontWeight: 700 }}>${stats.balances.totalUsdt.toLocaleString()}</div>
              <div style={{ fontSize: '10px', color: '#666' }}>USDT</div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{
        margin: '0 16px 20px',
        background: 'rgba(10, 22, 40, 0.6)',
        borderRadius: '12px',
        padding: '14px',
        border: '1px solid rgba(59, 130, 246, 0.2)',
      }}>
        <h3 style={{
          fontSize: '16px',
          fontWeight: 600,
          marginBottom: '12px',
          margin: '0 0 12px 0',
        }}>
          Quick Actions
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '10px',
        }}>
          <button
            onClick={() => onNavigate('users')}
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
              border: 'none',
              borderRadius: '10px',
              padding: '12px',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Manage Users
          </button>
          <button
            onClick={() => onNavigate('deposits')}
            style={{
              background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
              border: 'none',
              borderRadius: '10px',
              padding: '12px',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Review Deposits
          </button>
          <button
            onClick={() => onNavigate('broadcast')}
            style={{
              background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
              border: 'none',
              borderRadius: '10px',
              padding: '12px',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Send Broadcast
          </button>
          <button
            onClick={() => onNavigate('tasks')}
            style={{
              background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
              border: 'none',
              borderRadius: '10px',
              padding: '12px',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Manage Tasks
          </button>
          <button
            onClick={() => onNavigate('withdrawals')}
            style={{
              background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
              border: 'none',
              borderRadius: '10px',
              padding: '12px',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              position: 'relative',
            }}
          >
            💸 Withdrawals
            {stats.withdrawals.pending > 0 && (
              <span style={{
                position: 'absolute',
                top: '-6px',
                right: '-6px',
                background: '#ef4444',
                borderRadius: '50%',
                width: '22px',
                height: '22px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                fontWeight: 700,
              }}>
                {stats.withdrawals.pending}
              </span>
            )}
          </button>
          <button
            onClick={() => onNavigate('batteries')}
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              border: 'none',
              borderRadius: '10px',
              padding: '12px',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            🔋 Batteries
          </button>
          <button
            onClick={() => onNavigate('transfers')}
            style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
              border: 'none',
              borderRadius: '10px',
              padding: '12px',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            🔄 Transfers
          </button>
          <button
            onClick={() => onNavigate('staking')}
            style={{
              background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
              border: 'none',
              borderRadius: '10px',
              padding: '12px',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            🏦 Staking
          </button>
          <button
            onClick={() => onNavigate('ai-trader')}
            style={{
              background: 'linear-gradient(135deg, #e11d48 0%, #f43f5e 100%)',
              border: 'none',
              borderRadius: '10px',
              padding: '12px',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            🤖 AI Trader
          </button>
        </div>
      </div>
    </div>
  );
}